"use client";

import ErrorAlert from "@/components/alerts/errorsAlert";
import { api } from "@/lib/api";
import axios from "axios";
import { createContext, useContext, useEffect, useState } from "react";
import { Configuracion, User, UserContextType } from "./types";
import { logger } from "@/lib/logger";

const defaultContextValue: UserContextType = {
  user: null,
  setUser: () => {},
  configuracion: null,
  setConfiguracion: () => {},
  isLoadingUser: true,
  isLoggingOut: false,
  showingSessionExpiredAlert: false,
  logout: async () => {},
  loadConfiguracion: async () => {},
};

const UserContext = createContext<UserContextType>(defaultContextValue);

// Bandera que confirma que hubo una sesión autenticada real en esta pestaña.
// Se usa (en lugar de heurísticas basadas en cookies/localStorage no relacionados
// con el login) para decidir si mostrar el mensaje de "sesión expirada".
export const SESSION_FLAG_KEY = "ip_had_session";

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [configuracion, setConfiguracion] = useState<Configuracion | null>(
    null
  );
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showSessionExpiredAlert, setShowSessionExpiredAlert] = useState(false);
  const [sessionExpiredData, setSessionExpiredData] = useState({
    title: "Sesión Expirada",
    message:
      "Tu sesión ha expirado por seguridad. Por favor, inicia sesión nuevamente.",
  });

  const loadConfiguracion = async () => {
    try {
      const { data: res } = await api.get(
        "/api/dashboards/configuracion/sistem"
      );

      const config = res?.data || res;
      const tema = config?.tema || "light";

      // Normalizar el tema (corregir typo "ligth" a "light")
      const normalizedTema = tema === "dark" ? "dark" : "light";

      setConfiguracion({
        notificaciones_push: Boolean(config?.notificaciones_push ?? false),
        alertas_sonoras: Boolean(config?.alertas_sonoras ?? false),
        idioma: config?.idioma || "es",
        tema: normalizedTema,
      });

      // Sincronizar el tema con ThemeContext y localStorage
      if (typeof window !== "undefined") {
        localStorage.setItem("theme", normalizedTema);
        document.documentElement.setAttribute("data-theme", normalizedTema);
      }
    } catch (error) {
      logger.warn("⚠️ No se pudo cargar configuración:", error);
      setConfiguracion({
        notificaciones_push: false,
        alertas_sonoras: false,
        idioma: "es",
        tema: "light",
      });
    }
  };

  const logout = async () => {
    setIsLoggingOut(true);
    try {
      // Esperar al menos 3 segundos para mostrar la animación completa
      await Promise.all([
        api.post("/api/auth/logout"),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]);
    } finally {
      // Resetear el tema ANTES de limpiar localStorage
      if (typeof window !== "undefined") {
        // Disparar evento para que ThemeContext resetee el tema
        window.dispatchEvent(new Event("theme-reset"));

        // Forzar el reseteo del tema en el DOM
        document.documentElement.setAttribute("data-theme", "light");
      }

      // Limpiar COMPLETAMENTE sessionStorage
      if (typeof window !== "undefined") {
        sessionStorage.clear();
      }

      // Limpiar COMPLETAMENTE localStorage
      if (typeof window !== "undefined") {
        localStorage.clear();
      }

      // Limpiar todas las cookies del lado del cliente
      if (typeof document !== "undefined") {
        // Obtener todas las cookies
        const cookies = document.cookie.split(";");

        // Eliminar cada cookie
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf("=");
          const name =
            eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();

          // Eliminar la cookie en diferentes paths y dominios
          document.cookie =
            name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
          document.cookie =
            name +
            "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" +
            window.location.hostname;
        }
      }

      setUser(null);
      setConfiguracion(null);
      setIsLoggingOut(false);
    }
  };

  // 🔒 MANEJADOR DE EVENTOS DE SESIÓN EXPIRADA
  const handleSessionExpired = (event: Event) => {
    const customEvent = event as CustomEvent;

    // Actualizar datos de la alerta si vienen en el evento
    if (customEvent.detail) {
      setSessionExpiredData({
        title: customEvent.detail.title || "Sesión Expirada",
        message:
          customEvent.detail.message || "Tu sesión ha expirado por seguridad.",
      });
    }

    // Resetear el tema ANTES de limpiar
    if (typeof window !== "undefined") {
      // Disparar evento para que ThemeContext resetee el tema
      window.dispatchEvent(new Event("theme-reset"));

      // Forzar el reseteo del tema en el DOM
      document.documentElement.setAttribute("data-theme", "light");
    }

    // Limpiar sessionStorage y localStorage INMEDIATAMENTE
    if (typeof window !== "undefined") {
      sessionStorage.clear();
      localStorage.clear();

      // Limpiar todas las cookies del lado del cliente
      if (typeof document !== "undefined") {
        const cookies = document.cookie.split(";");
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf("=");
          const name =
            eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();

          // Eliminar la cookie en diferentes paths y dominios
          document.cookie =
            name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
          document.cookie =
            name +
            "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" +
            window.location.hostname;
        }
      }
    }

    // Limpiar el usuario del contexto
    setUser(null);
    setConfiguracion(null);

    // Mostrar alerta (se cerrará automáticamente después de 5 segundos)
    setShowSessionExpiredAlert(true);
  };

  // Verificar usuario al cargar la aplicación
  useEffect(() => {
    const initializeUser = async () => {
      setIsLoadingUser(true);

      // Verificar si había una sesión previa autenticada en esta pestaña.
      // Se usa una bandera explícita (SESSION_FLAG_KEY) en lugar de inferirlo de
      // cookies que esta app ya no usa (XSRF-TOKEN/laravel_session son de una
      // arquitectura anterior; la sesión actual usa cookies httpOnly auth_token/
      // user_id que ni siquiera son visibles desde JS) o de claves de
      // sessionStorage/localStorage no relacionadas con el login (tema, progreso
      // de agendamiento, etc.), que generaban falsos positivos de "sesión expirada".
      const hadPreviousSession =
        typeof window !== "undefined" &&
        sessionStorage.getItem(SESSION_FLAG_KEY) === "true";

      const clean = (value: unknown): unknown => {
        if (value === null || value === undefined) return "";
        if (typeof value === "object") return value;
        if (typeof value !== "string") return value;

        const trimmed = value.trim();

        // ⬇️ normalización defensiva
        if (trimmed === "." || trimmed === "-" || trimmed === "N/A") {
          return "";
        }

        return trimmed;
      };

      try {
        // Intentar obtener el usuario desde las cookies
        const { data } = await api.get("/api/auth/me");

        if (data.user && data.user.data) {
          const userData = data.user.data;

          //  LIMPIA TODOS LOS "." AUTOMÁTICAMENTE
          const cleanedUser = Object.fromEntries(
            Object.entries(userData).map(([key, value]) => [
              key,
              typeof value === "string" ? clean(value) : value,
            ])
          ) as typeof userData;

          //  Construir nombre visible sin puntos
          const mappedUser = {
            userId: cleanedUser.idpaciente,
            type: "paciente",
            name: `${cleanedUser.primer_nombre} ${cleanedUser.segundo_nombre} ${cleanedUser.primer_apellido} ${cleanedUser.segundo_apellido}`
              .replace(/\s+/g, " ") // evita espacios dobles
              .trim(),
            ...cleanedUser, // ya viene limpio
          };

          setUser(mappedUser);

          // Cargar configuración después de obtener el usuario
          await loadConfiguracion();
        } else {
          setUser(null);
        }
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          // Si había una sesión previa, significa que el token expiró al recargar
          if (hadPreviousSession && typeof window !== "undefined") {
            // Resetear el tema ANTES de limpiar
            window.dispatchEvent(new Event("theme-reset"));
            document.documentElement.setAttribute("data-theme", "light");

            // Limpiar datos
            sessionStorage.clear();
            localStorage.clear();

            // Limpiar cookies
            if (typeof document !== "undefined") {
              const cookies = document.cookie.split(";");
              for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i];
                const eqPos = cookie.indexOf("=");
                const name =
                  eqPos > -1
                    ? cookie.substring(0, eqPos).trim()
                    : cookie.trim();
                document.cookie =
                  name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
                document.cookie =
                  name +
                  "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" +
                  window.location.hostname;
              }
            }

            // Actualizar datos de la alerta y mostrarla DIRECTAMENTE (no usar evento)
            setSessionExpiredData({
              title: "Sesión Expirada",
              message:
                "Tu sesión ha expirado. Por favor, inicia sesión nuevamente.",
            });
            setShowSessionExpiredAlert(true);
          }
        } else if (axios.isAxiosError(err)) {
          console.error("❌ Error al verificar usuario:", err);
        }
        setUser(null);
      } finally {
        setIsLoadingUser(false);
      }
    };

    initializeUser();

    // Solo escuchar en el cliente
    if (typeof window !== "undefined") {
      window.addEventListener("session-expired", handleSessionExpired);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("session-expired", handleSessionExpired);
      }
    };
  }, []);

  // Marcar que hubo una sesión autenticada real en esta pestaña, para que un
  // futuro 401 se interprete como "expiró" y no como "nunca hubo sesión".
  useEffect(() => {
    if (user && typeof window !== "undefined") {
      sessionStorage.setItem(SESSION_FLAG_KEY, "true");
    }
  }, [user]);

  // 🔄 VALIDACIÓN PERIÓDICA DEL TOKEN (cada 5 minutos)
  useEffect(() => {
    if (!user) return; // Solo validar si hay usuario activo

    const validateToken = async () => {
      try {
        await api.get("/api/auth/me");
      } catch (error) {
        // Si es un 401, el token expiró - disparar evento de sesión expirada
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          // Disparar evento personalizado para mostrar alerta (NO limpiar aquí, handleSessionExpired lo hará)
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("session-expired", {
                detail: {
                  title: "Sesión Expirada",
                  message:
                    "Tu sesión ha expirado por inactividad. Por favor, inicia sesión nuevamente.",
                },
              })
            );
          }
        }
      }
    };

    // Validar cada 5 minutos (300000 ms)
    const intervalId = setInterval(validateToken, 300000);

    return () => clearInterval(intervalId);
  }, [user]);

  // Manejar cierre de alerta de sesión expirada
  const handleCloseSessionExpiredAlert = () => {
    setShowSessionExpiredAlert(false);

    // Redirigir al login usando window.location para forzar recarga completa
    if (typeof window !== "undefined") {
      const basePath = process.env.NEXT_PUBLIC_BASE_PATH || "";
      window.location.href = `${basePath}/login?session_expired=true`;
    }
  };

  return (
    <>
      {showSessionExpiredAlert && (
        <ErrorAlert
          title={sessionExpiredData.title}
          message={sessionExpiredData.message}
          onClose={handleCloseSessionExpiredAlert}
        />
      )}
      <UserContext.Provider
        value={{
          user,
          setUser,
          configuracion,
          setConfiguracion,
          isLoadingUser,
          isLoggingOut,
          showingSessionExpiredAlert: showSessionExpiredAlert,
          logout,
          loadConfiguracion,
        }}
      >
        {children}
      </UserContext.Provider>
    </>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    // En producción, retornar valores por defecto seguros en lugar de lanzar error
    // para evitar crashes durante la hidratación o código minificado
    if (
      typeof window !== "undefined" &&
      process.env.NODE_ENV === "production"
    ) {
      logger.warn(
        "useUser: Contexto no encontrado, retornando valores por defecto"
      );
      return {
        user: null,
        setUser: () => {},
        configuracion: null,
        setConfiguracion: () => {},
        isLoadingUser: true,
        isLoggingOut: false,
        showingSessionExpiredAlert: false,
        logout: async () => {},
        loadConfiguracion: async () => {},
      };
    }
    throw new Error("Debe estar dentro del provider");
  }
  return context;
};
